import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Segmented, Space, Spin, Typography, theme } from "antd";
import { AppstoreOutlined, LeftOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import Timeline, {
  DateHeader,
  SidebarHeader,
  TimelineHeaders,
  TimelineMarkers,
  TodayMarker
} from "react-calendar-timeline";
import "react-calendar-timeline/lib/Timeline.css";
import moment from "moment";
import { useTranslation } from "react-i18next";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { hasPermission } from "../../../commons/Permissions/PermissionsHook";
import { UserContext } from "../../../context/UserContext";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import ModalAddRoom from "./ModalAddRoom";
import ModalRoom from "./ModalRoom";
import { listBookings, listRooms } from "./api";
import "./RoomManagment.css";

// Same key the route guard derives from /user/room-management.
const MODULE = "Room-management";
const HALF_HOUR = 30 * 60 * 1000;

const keys = {
  groupIdKey: "id",
  groupTitleKey: "title",
  groupRightTitleKey: "rightTitle",
  itemIdKey: "id",
  itemTitleKey: "title",
  itemDivTitleKey: "tooltip",
  itemGroupKey: "group",
  itemTimeStartKey: "start_time",
  itemTimeEndKey: "end_time"
};

const rangeFor = (zoom, anchor = moment()) => {
  const start = moment(anchor).startOf(zoom === "week" ? "isoWeek" : zoom);
  return { start: start.valueOf(), end: moment(start).add(1, zoom).valueOf() };
};

const RoomManagment = () => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const user = useContext(UserContext);
  const can = useCallback(action => hasPermission(user?.permissions, MODULE, action), [user]);

  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [zoom, setZoom] = useState("day");
  const [visible, setVisible] = useState(() => rangeFor("day"));
  const [roomsOpen, setRoomsOpen] = useState(false);
  // { booking } to edit, { defaults } to add, null when closed.
  const [bookingModal, setBookingModal] = useState(null);

  // The loaded window is wider than the visible one, so scrolling a little needs no request.
  const fetched = useRef(null);
  const requestId = useRef(0);

  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await listRooms();
      setRooms(res.data.data || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
      generalErrorAlert(err);
    } finally {
      setLoadingRooms(false);
    }
  };

  const loadBookings = async range => {
    const id = ++requestId.current;
    setLoadingBookings(true);
    try {
      const res = await listBookings(range.start, range.end);
      if (id !== requestId.current) return;
      fetched.current = range;
      setBookings(res.data.data || []);
      setLoadError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      setLoadError(err);
      generalErrorAlert(err);
    } finally {
      if (id === requestId.current) setLoadingBookings(false);
    }
  };

  const reloadBookings = () => {
    const span = visible.end - visible.start;
    loadBookings({ start: visible.start - span, end: visible.end + span });
  };

  useEffect(() => {
    loadRooms();
  }, []);

  useEffect(() => {
    const range = fetched.current;
    if (range && visible.start >= range.start && visible.end <= range.end) return undefined;
    const timer = setTimeout(reloadBookings, 250);
    return () => clearTimeout(timer);
  }, [visible.start, visible.end]);

  const reloadAll = () => {
    loadRooms();
    reloadBookings();
  };

  const groups = useMemo(
    () =>
      rooms.map(room => ({
        id: room._id,
        title: room.name,
        capacity: room.capacity,
        active: room.active
      })),
    [rooms]
  );

  const itemStyle = {
    background: token.colorPrimary,
    borderColor: token.colorPrimaryActive,
    color: token.colorTextLightSolid,
    borderRadius: token.borderRadiusSM
  };

  const items = useMemo(
    () =>
      bookings
        .filter(b => b.room?._id)
        .map(b => {
          const start = moment(b.start);
          const end = moment(b.end);
          const who = [b.employee?.firstName, b.employee?.lastName].filter(Boolean).join(" ");
          return {
            id: b._id,
            group: b.room._id,
            title: b.title,
            tooltip: `${b.title} · ${start.format("DD.MM. HH:mm")} – ${end.format("HH:mm")}${
              who ? ` · ${who}` : ""
            }`,
            start_time: start,
            end_time: end,
            itemProps: { className: "room-management-page__booking", style: itemStyle }
          };
        }),
    [bookings, token]
  );

  const openBooking = itemId => {
    const booking = bookings.find(b => b._id === itemId);
    if (booking) setBookingModal({ booking });
  };

  const onCanvasClick = (groupId, time) => {
    const room = rooms.find(r => r._id === groupId);
    if (!room?.active || !can("Create")) return;
    setBookingModal({
      defaults: { room: room._id, start: new Date(Math.floor(time / HALF_HOUR) * HALF_HOUR) }
    });
  };

  const changeZoom = value => {
    setZoom(value);
    setVisible(rangeFor(value, moment((visible.start + visible.end) / 2)));
  };

  const shift = direction => {
    const span = visible.end - visible.start;
    setVisible({ start: visible.start + direction * span, end: visible.end + direction * span });
  };

  const onSaved = () => {
    setBookingModal(null);
    reloadBookings();
  };

  const rangeLabel = (() => {
    const start = moment(visible.start);
    const end = moment(visible.end - 1);
    if (start.isSame(end, "day")) return start.format("ddd, DD.MM.YYYY");
    return `${start.format("DD.MM.")} – ${end.format("DD.MM.YYYY")}`;
  })();

  const noRooms = !loadingRooms && !loadError && rooms.length === 0;

  return (
    <div className="room-management-page">
      <PageToolbar
        filters={
          <div className="room-management-page__nav">
            <Space.Compact>
              <Button
                icon={<LeftOutlined />}
                onClick={() => shift(-1)}
                aria-label={t("Previous")}
              />
              <Button onClick={() => setVisible(rangeFor(zoom))}>{t("Today")}</Button>
              <Button icon={<RightOutlined />} onClick={() => shift(1)} aria-label={t("Next")} />
            </Space.Compact>
            <Segmented
              value={zoom}
              onChange={changeZoom}
              options={[
                { value: "day", label: t("Day") },
                { value: "week", label: t("Week") },
                { value: "month", label: t("Month") }
              ]}
            />
            <Typography.Text strong className="room-management-page__range">
              {rangeLabel}
            </Typography.Text>
          </div>
        }
        actions={
          <>
            <Button icon={<AppstoreOutlined />} onClick={() => setRoomsOpen(true)}>
              {t("Rooms")}
            </Button>
            {can("Create") && (
              <MainButtonComp
                text="Add booking"
                addBtn
                disabled={!rooms.some(r => r.active)}
                onClick={() => setBookingModal({ defaults: {} })}
              />
            )}
          </>
        }
      />

      {loadError && (
        <Alert
          className="room-management-page__alert"
          type="error"
          showIcon
          message={t("Rooms or bookings could not be loaded.")}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={reloadAll}>
              {t("Try again")}
            </Button>
          }
        />
      )}

      {loadingRooms && rooms.length === 0 ? (
        <div className="room-management-page__state">
          <Spin />
        </div>
      ) : noRooms ? (
        <div className="room-management-page__state">
          <Empty description={t("No rooms yet. Add your first room to start taking bookings.")}>
            {can("Create") && (
              <Button type="primary" onClick={() => setRoomsOpen(true)}>
                {t("Add room")}
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        rooms.length > 0 && (
          <Spin spinning={loadingBookings} delay={200}>
            <div className="room-management-page__timeline">
              <Timeline
                groups={groups}
                items={items}
                keys={keys}
                visibleTimeStart={visible.start}
                visibleTimeEnd={visible.end}
                onTimeChange={(start, end) => setVisible({ start, end })}
                sidebarWidth={isMobile ? 104 : 180}
                lineHeight={isMobile ? 48 : 40}
                itemHeightRatio={0.8}
                canMove={false}
                canResize={false}
                stackItems
                itemTouchSendsClick
                onItemSelect={openBooking}
                onItemClick={openBooking}
                onCanvasClick={onCanvasClick}
                groupRenderer={({ group }) => (
                  <div className="room-management-page__group" title={group.title}>
                    <span className="room-management-page__group-name">{group.title}</span>
                    {(group.capacity != null || !group.active) && (
                      <span className="room-management-page__group-meta">
                        {!group.active
                          ? t("Inactive")
                          : t("{{n}} people", { n: group.capacity })}
                      </span>
                    )}
                  </div>
                )}
              >
                <TimelineHeaders className="sticky">
                  <SidebarHeader>
                    {({ getRootProps }) => (
                      <div {...getRootProps()}>
                        <span
                          className="room-management-page__sidebar-title"
                          style={{ color: token.colorTextLightSolid }}
                        >
                          {t("Rooms")}
                        </span>
                      </div>
                    )}
                  </SidebarHeader>
                  <DateHeader unit="primaryHeader" />
                  <DateHeader />
                </TimelineHeaders>
                <TimelineMarkers>
                  <TodayMarker>
                    {({ styles }) => (
                      <div style={{ ...styles, backgroundColor: token.colorError, width: 2 }} />
                    )}
                  </TodayMarker>
                </TimelineMarkers>
              </Timeline>
            </div>
            {can("Create") && (
              <Typography.Text type="secondary" className="room-management-page__hint">
                {t("Tap an empty slot to book it, or a booking to edit it.")}
              </Typography.Text>
            )}
          </Spin>
        )
      )}

      <ModalRoom
        open={roomsOpen}
        rooms={rooms}
        loading={loadingRooms}
        can={can}
        onClose={() => setRoomsOpen(false)}
        onChanged={reloadAll}
      />
      <ModalAddRoom
        open={Boolean(bookingModal)}
        booking={bookingModal?.booking || null}
        defaults={bookingModal?.defaults}
        rooms={rooms}
        canUpdate={can("Update")}
        canDelete={can("Delete")}
        onClose={() => setBookingModal(null)}
        onSaved={onSaved}
      />
    </div>
  );
};

export default RoomManagment;
